import React, { useState, useEffect, useRef } from 'react';
import TextTransition, { presets } from "react-text-transition";
import queryString from 'querystring';
import useDebounce from "../../hooks/useDebounce";
import ResultItem from "./ResultItem";
import ShopItems from "./ShopItems";
import CategoriesDrawer from "./CategoriesDrawer";
import ShopPreview from "../../map/components/ShopPreview";
import {getParameterFromQuery} from "../../../utils/parseQuery";
import t from '../../../utils/t';

function parseData(data) {
  return Object.keys(data).map(key => {
    const dataToReturn = {};

    dataToReturn.items = data[key].filter(x => x.translation).map(e => ({...e, nameId: key}));

    if (!data[key].length || key === 'articles') return null;
    if (!data[key].length || key === 'stores') return null;

    if (key === 'stores') {
      dataToReturn.title = "Üzletek";
      dataToReturn.nameSelector = "name";
      dataToReturn.urlSelector = "url";
    }

    if (key === 'categories') {
      dataToReturn.title = t('stores.attributes.categories');
    }

    if (key === 'tags') {
      dataToReturn.title = t('stores.attributes.tags');
    }

    return dataToReturn;
  }).filter(e => e);
}

const SearchField = (props = {}) => {
  const { isMap, ActiveShopPreview, mapState, mapDispatch } = props;
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [defaultData, setDefaultData] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [focus, setFocus] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tag, setTag] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [stores, setStoresLocal] = useState([]);
  const [data, setData] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [voucher, setVoucher] = useState(!!(window.options.filters.voucher || false));
  const [taxFree, setTaxFree] = useState(!!(window.options.filters.tax_free || false));
  const [giftCard, setGiftCard] = useState(!!(window.options.filters.gift_card || false));
  const $activeCategory = useRef(null);
  const $activeTag = useRef(null);
  const $input = useRef(null);
  const $resultsContainer = useRef(null);
  const $shopsWrapper = useRef(null);
  let latestRequest = null;

  const setStores = (stores) => {
    if (isMap) {
      return mapDispatch({ type: 'setSelectedShops', selectedShops: stores })
    }

    setStoresLocal(stores);
  };

  const debouncedKeyword = useDebounce(keyword, 500);

  async function getCategories() {
    const { lang } = document.querySelector('html');
    const response = await fetch(`${window.location.origin}/api/categories?lang_id=${lang}`);
    const { data, error } = await response.json();

    if (!error && data && !allCategories.length) {
      setAllCategories(data);
      const defaultCategory = getParameterFromQuery('category', window.location.search);
      const defaultTag = getParameterFromQuery('tag', window.location.search);
      if (defaultCategory) {
        const cat = data.find(e => e.id === Number(defaultCategory));

        if (!cat) {
          const children = data.find(e => e.children.find(e => e.id === Number(defaultCategory))).children;
          const subCat = children.find(e => e.id === Number(defaultCategory));
          // setTimeout(() => {
          //   setCategories(children);
          // }, 500);
          return setActiveSubCategory(subCat);
        }
        setActiveCategory(cat);
      }

      if (defaultTag) {
        const resp = await fetch(`${window.location.origin}/api/tags?lang_id=${lang}${defaultTag}`);
        const tagData = await resp.json();
        setTag(tagData.data);
      }
    }
  }

  function resetInputPadding() {
    $input.current.style.paddingLeft = `48px`;
  }

  function resetSearch() {
    setTag(null);
    setActiveCategory(null);
    setActiveSubCategory(null);
    setKeyword('');
    resetInputPadding();
    setFocus(false);
  }

  function getTagWidth(text) {
    const $wrapper = document.createElement('div');
    $wrapper.style.opacity = 0;
    $wrapper.classList.add('search-field-tag', 'is-active');
    $wrapper.innerHTML = text;
    document.body.appendChild($wrapper);
    const width = $wrapper.getBoundingClientRect().width;
    $wrapper.remove();
    return width;
  }

  function handleKeyPress(e) {
    const isBackspace = e.keyCode === 8;

    if (isBackspace && !keyword) {
      resetSearch();
    }
  }

  function handleCategoryChoose(id) {
    const category = categories.find(e => e.id === id);
    if (category.is_parent) {
      return setActiveCategory(category);
    }

    if (activeSubCategory && activeSubCategory.id === id) {
      return setActiveSubCategory(null)
    }
    setActiveSubCategory(category)
  }

  async function getDefaultData() {
    const { lang } = document.querySelector('html');
    setLoading(true);

    if (defaultData) {
      setData(parseData(defaultData));

      if (!activeSubCategory) setCategories(defaultData.categories.filter(e => e.is_parent));

      if (isMap) {
        setStores(mapState.shops);
      } else {
        setStores(defaultData.stores);
      }

      setTag(null);
      setVoucher(false);
      setTaxFree(false);
      setGiftCard(false);

      setLoading(false);
      return;
    }

    let url = `${window.location.origin}/api/search?lang_id=${lang}`;

    if (voucher) {
      url += `&voucher=1`;
    }

    if (taxFree) {
      url += `&tax_free=1`;
    }

    if (giftCard) {
      url += `&gift_card=1`;
    }

    let defaultCategory = getParameterFromQuery('category', window.location.search);

    if (defaultCategory) {
      url += `&categories[]=${defaultCategory}`;
    }

    const response = await fetch(url);
    const { data, error } = await response.json();

    if (!error && data) {
      setCategories(data.categories.filter(e => e.is_parent));
      setStores(data.stores);
      setData(parseData(data));
      setDefaultData({...data});
    }

    setLoading(false);
  }

  async function handleSearch() {
    const { lang } = document.querySelector('html');
    const requestId = Math.floor(Math.random() * 1000000);

    if (keyword.length < 2 && !activeCategory && !activeSubCategory && !tag && !voucher && !taxFree && !giftCard) {
      getDefaultData();
      return null;
    }

    latestRequest = requestId;
    setLoading(true);

    if (activeCategory) {
      setCategories(allCategories.find(e => e.id === activeCategory.id).children)
    }

    const query = { keyword };

    if (activeCategory) query['categories[0]'] = activeCategory.id;
    if (activeSubCategory) query['categories[0]'] = activeSubCategory.id;
    if (tag) query['tags[0]'] = tag.id;
    if (voucher) query['voucher'] = 1;
    if (giftCard) query['gift_card'] = 1;
    if (taxFree) query['tax_free'] = 1;

    const response = await fetch(`${window.location.origin}/api/search?${queryString.stringify(query)}&lang_id=${lang}`);
    const { data, error } = await response.json();

    if (latestRequest === requestId) {
      if (!error && data) {
        setStores(data.stores);

        const parsedDatas = parseData(data);

        setData(parsedDatas);
      }

      setLoading(false);
    }
  }

  const showTags = categories && categories.length > 0 && !focus;
  const showResults = data && data.length !== 0 && focus && !activeCategory;

  useEffect(() => {
    getDefaultData();
    getCategories();
    if (!initialized) setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized) {
      handleSearch();
    }
  }, [debouncedKeyword, activeCategory, tag, voucher, taxFree, giftCard]);

  useEffect(() => {
    if (activeSubCategory) {
      const parentCategory = allCategories.find(cat => cat.children.find(e => e.id === activeSubCategory.id));
      setTag(null);

      setActiveCategory(parentCategory);
      setCategories(parentCategory.children);
      handleSearch();
    }

    if (activeCategory && !activeSubCategory) {
      handleSearch();
    }
  }, [activeSubCategory]);

  useEffect(() => {
    if (activeCategory) {
      setTag(null);
    }

    if (mapDispatch) mapDispatch({ type: 'setActiveShopPreview', activeShopPreview: null });

    if ($activeCategory.current && activeCategory) {
      const tagWidth = getTagWidth(activeCategory.translation.title);
      $input.current.style.paddingLeft = `${48 + 8 + tagWidth}px`;

      if (activeCategory) {
        setKeyword('');
        $activeCategory.current.style.width = `${tagWidth}px`;
      }
    }
  }, [activeCategory]);

  useEffect(() => {
    if ($activeTag.current && tag) {
      const tagWidth = getTagWidth(tag.translation.title);
      $input.current.style.paddingLeft = `${48 + 8 + tagWidth}px`;

      if (tag) {
        setKeyword('');
        $activeTag.current.style.width = `${tagWidth}px`;
      }
    }
  }, [tag]);

  // useEffect(() => {
  //   if ($shopsWrapper.current) {
  //     const TITLE_HEIGHT = 11;
  //     const ITEM_HEIGHT = 40;
  //     const DEFAULT_PADDING = 10;
  //
  //     let height = (data.length * TITLE_HEIGHT) + (data.reduce((a, b) => a + b.items.length, 0) * ITEM_HEIGHT) + DEFAULT_PADDING;
  //
  //     if (height > 300) height = 300;
  //     if (height === DEFAULT_PADDING) height = 0;
  //
  //     if (showResults) {
  //       $shopsWrapper.current.style.paddingTop = `${height}px`;
  //     } else {
  //       $shopsWrapper.current.style.paddingTop = `${0}px`;
  //     }
  //   }
  // }, [showResults, data]);

  return(
    <React.Fragment>
      {(!mapState || !mapState.activeShopPreview) && (
        <React.Fragment>
          <div className={`search-field with-toggler control ${focus ? 'is-active' : ''} ${loading ? 'is-loading' : ''}`}>
            <div className="search-field__inner">
              <span className="border" />
              <input
                onChange={(e) => {
                  setKeyword(e.target.value);
                  if (!focus) setFocus(true);
                }}
                onFocus={() => setFocus(true)}
                onBlur={(e) => {
                  setTimeout(() => {
                    setFocus(false)
                  }, 100)
                }}
                onKeyDown={handleKeyPress}
                value={keyword}
                className="input"
                placeholder={t('stores.search.searching')}
                ref={$input}
              />
              <a onClick={resetSearch} className={`search-field__close ${activeCategory || keyword || focus ? 'is-active' : ''}`} />
              <a ref={$activeCategory} className={`search-field-tag ${activeCategory ? 'is-active' : ''}`}>
                {activeCategory && !tag && (
                  <TextTransition
                    text={activeCategory.translation.title}
                    order={activeCategory.translation.title}
                    spring={presets.molasses}
                  />
                )}
              </a>
              <a ref={$activeTag} className={`search-field-tag ${tag ? 'is-active' : ''}`}>
                {tag && (
                  <TextTransition
                    text={tag.translation.title}
                    order={tag.translation.title}
                    spring={presets.molasses}
                  />
                )}
              </a>

              <div ref={$resultsContainer} className={`search-field__results ${showResults ? 'is-active' : ''}`}>
                {data && data.length > 0 && data.map(item => (
                  <ResultItem
                    setActiveCategory={setActiveCategory}
                    key={item.title}
                    setTag={setTag}
                    {...item}
                  />
                ))}
              </div>
            </div>
            <div className="search-field__toggler" onClick={() => setDrawerOpen(true)}>
              <span className="icomoon-filter" />
            </div>
          </div>
          <div className={`search-field__tags ${showTags ? 'is-active' : ''}`}>
            {categories && categories.length > 0 && categories.map(({ translation, id }) => {
              if (!translation) return null;

              return (
                <a key={id} onClick={() => handleCategoryChoose(id)} className={`tag ${activeSubCategory && id === activeSubCategory.id ? 'is-active' : ''}`}>{translation.title}</a>
              )
            })}
          </div>
        </React.Fragment>
      )}
      <div ref={$shopsWrapper}>
        {!loading ? (
          <ShopItems
            isMap={isMap}
            activeCategory={activeCategory}
            mapState={mapState}
            mapDispatch={mapDispatch}
            stores={stores}
            keyword={keyword}
          />
        ) : (
          <div className="search-field__loading"/>
        )}
      </div>
      {ActiveShopPreview && <ActiveShopPreview />}
      <CategoriesDrawer
        open={drawerOpen}
        categories={allCategories}
        setOpen={setDrawerOpen}
        setActiveSubCategory={setActiveSubCategory}
        setActiveCategory={setActiveCategory}
        taxFree={taxFree}
        setTaxFree={setTaxFree}
        giftCard={giftCard}
        setGiftCard={setGiftCard}
        voucher={voucher}
        setVoucher={setVoucher}
      />
    </React.Fragment>
  )
};

export default SearchField;



// WEBPACK FOOTER //
// ./resources/assets/js/components/shop-search/components/SearchField.jsx